import { getPositionSeconds } from "./positionSeconds";
import { fetchPositions, isSignedIn } from "./progressSync";

/*
 * Per-class progress for category pages. Sources, furthest-wins:
 *   server — GET /user/positions rows (class_id, position_seconds,
 *   duration_seconds), ordered most-recent-first; signed-in only.
 *   device — the `ic-class-media:{slug}` marker each class page writes,
 *   pointing at the media URL whose localStorage position to read (the same
 *   inference the series page ships).
 * Duration prefers the category payload's own durationSeconds (ordered=1
 * backend), then the server row; without one, "completed" can't be proven
 * so a started class only counts as in-progress.
 */

const COMPLETED_AT = 0.9;

export function readDevicePosition(slug) {
  try {
    const marker = localStorage.getItem(`ic-class-media:${slug}`);
    return marker ? getPositionSeconds(marker) : 0;
  } catch (_) {
    return 0;
  }
}

/**
 * @param {Array} classes  category children of type 'class' (curated order)
 * @param {Array|null} serverRows  /user/positions rows, most recent first
 * @param {(slug: string) => number} readDevice  injectable for tests
 * @returns {{byId: Object, currentId: string|null, resume: object|null,
 *            startedCount: number, completedCount: number}}
 */
export function deriveCategoryProgress(classes, serverRows, readDevice = readDevicePosition) {
  const serverById = new Map();
  for (const r of serverRows || []) {
    const id = String(r.class_id);
    if (!serverById.has(id)) serverById.set(id, r); // first row = most recent
  }

  const byId = {};
  let startedCount = 0;
  let completedCount = 0;

  classes.forEach((c) => {
    const s = serverById.get(String(c.id));
    const position = Math.max(readDevice(c.slug), s ? Number(s.position_seconds) || 0 : 0);
    if (!(position > 0)) return;
    const duration = c.durationSeconds || (s ? Number(s.duration_seconds) || 0 : 0) || 0;
    const completed = duration > 0 && position >= duration * COMPLETED_AT;
    startedCount += 1;
    if (completed) completedCount += 1;
    byId[String(c.id)] = {
      position,
      duration,
      completed,
      pct: duration > 0 ? Math.min(100, Math.round((position / duration) * 100)) : null,
    };
  });

  // Current class: the most recently played in-progress one (server row
  // order carries recency); the device store has no timestamps, so its
  // fallback is the furthest-along started class in curated order — the
  // same heuristic the series page uses.
  let currentId = null;
  for (const r of serverRows || []) {
    const id = String(r.class_id);
    if (byId[id] && !byId[id].completed) {
      currentId = id;
      break;
    }
  }
  if (!currentId) {
    for (const c of classes) {
      const id = String(c.id);
      if (byId[id] && !byId[id].completed) currentId = id;
    }
  }

  const resume = currentId ? classes.find((c) => String(c.id) === currentId) || null : null;
  return { byId, currentId, resume, startedCount, completedCount };
}

/** Convenience: server rows when signed in, else null (device-only). */
export async function loadServerPositions() {
  if (!isSignedIn()) return null;
  return fetchPositions();
}
